import {
  PaymentMethodType as VoPaymentMethodType,
  PaymentMethod as VoPaymentMethod,
  SupportedCurrencies,
  VerificationStatus,
  CardBrand,
  toBool,
  formatPhoneNumber,
} from 'vopay';

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useDataFetchIndicator } from '.';

import vopayApi from '@/services/api.config';
import { useClientData } from '@/providers/ClientData.provider';

const { api } = vopayApi;

export type AddInteracParams = {
  question: string;
  answer: string;
  email: string;
  phone: string;
  recipientName: string;
  accountID?: string;
};

export type PaymentMethodType = VoPaymentMethodType;
type Kind = VoPaymentMethod['ExtraFields']['RecipientType'] | 'HOLDER';

export type PaymentMethod = {
  type: PaymentMethodType;
  accountHolderName?: string;
  typeLabel: string;
  kind?: Kind;
  groupedItems?: PaymentMethod[];
  addedOn: string;
  details: string;
  isDefault: boolean;
  token: string;
  currency: SupportedCurrencies | null;
  verificationStatus?: VerificationStatus;
  institutionName?: string;
  creditCardBrand?: CardBrand;
  expirationDate?: string;
  bankId?: string;
  verificationSentOn?: string;
  hasQuestionAndAnswer?: boolean;
  paymentRails?: string;
};

type HookProps = {
  accountID?: string;
  currencies?: SupportedCurrencies[];
};

const creditCardMap: Record<CardBrand, string> = {
  amex: 'Amex',
  visa: 'Visa',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  uatp: 'UATP',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  unknown: 'Card',
};

export default function usePaymentMethods(
  { accountID, currencies = ['CAD'] }: HookProps = {
    currencies: ['CAD'],
  },
) {
  const { response: loginUser } = useClientData();
  const loginUserName = loginUser.LoginUserName;
  const clientAccountsEnabled = toBool(loginUser.ClientAccountsEnabled);

  const source = useRef(axios.CancelToken.source());
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [groupedMethods, setGroupedMethods] = useState<PaymentMethod[]>([]);
  const CUSTOMER_ID_PROP = clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const [, setPending] = useDataFetchIndicator();

  useEffect(() => {
    source.current.cancel();
  }, []);

  const deleteMethod = (method: PaymentMethod, _accountID = accountID) => {
    if (!_accountID) {
      return {
        Success: false,
        ErrorMessage: 'Account ID is required',
      };
    }

    if (method.type === 'PayPal') {
      return deletePaypal(method.token);
    }

    if (method.type === 'Venmo') {
      return deleteVenmo(method.token);
    }

    if (method.type === 'Zelle') {
      return deleteZelle(method.token);
    }

    if (method.type === 'BankAccount') {
      return deleteBankAccount(method.token);
    }

    if (method.type === 'Email' || method.type === 'PhoneNumber') {
      return deleteInterac(method.token, _accountID);
    }

    if (method.type === 'CreditCard') {
      return deleteCreditCard(method.token);
    }

    if (method.type === 'DebitCard') {
      return deleteDebitCard(method.token);
    }

    return {
      Success: false,
      ErrorMessage: 'Invalid payment method',
    };
  };

  const deleteGroupedMethod = async (method: PaymentMethod, _accountID = accountID) => {
    if (!_accountID) {
      return [
        {
          Success: false,
          ErrorMessage: 'Account ID is required',
        },
      ];
    }

    const deletePromises = [];

    if (method.kind !== 'HOLDER') {
      deletePromises.push(deleteMethod(method, _accountID));
    }

    if (method.groupedItems) {
      for (const groupedItem of method.groupedItems) {
        deletePromises.push(deleteMethod(groupedItem, _accountID));
      }
    }

    try {
      const results = await Promise.all(deletePromises);
      return results;
    } catch (error) {
      console.error('Error deleting grouped methods:', error);
      return [
        {
          Success: false,
          ErrorMessage: 'Unexpected error while deleting grouped methods',
        },
      ];
    }
  };

  function deleteBankAccount(Token: string) {
    try {
      setPending(true);

      return api.postDeleteClientBankAccount(
        {
          PortalUser: loginUserName,
          Token,
        },
        source.current,
      );
    } finally {
      setPending(false);
    }
  }

  function deletePaypal(PayPalToken: string) {
    try {
      setPending(true);

      return api.postDeletePaypalAccount({ PayPalToken }, source.current);
    } finally {
      setPending(false);
    }
  }

  function deleteVenmo(VenmoToken: string) {
    try {
      setPending(true);

      return api.postDeleteVenmoAccount({ VenmoToken }, source.current);
    } finally {
      setPending(false);
    }
  }

  function deleteZelle(ZelleToken: string) {
    try {
      setPending(true);

      return api.deleteZelleAccount({ ZelleToken }, source.current);
    } finally {
      setPending(false);
    }
  }

  function deleteInterac(Token: string, accountID: string) {
    try {
      setPending(true);

      // TODO: b/e to accept ContactID as well
      return api.postDeleteInteracEmail({ Token, ClientAccountID: accountID }, source.current);
    } finally {
      setPending(false);
    }
  }

  function deleteCreditCard(CreditCardToken: string) {
    try {
      setPending(true);

      return api.postDeleteClientCreditCard(
        {
          PortalUser: loginUserName,
          CreditCardToken,
        },
        source.current,
      );
    } finally {
      setPending(false);
    }
  }

  function deleteDebitCard(CardToken: string) {
    try {
      setPending(true);

      return api.postDeleteDebitCard({ CardToken }, source.current);
    } finally {
      setPending(false);
    }
  }

  const [fetching, setFetching] = useState(false);
  const fetchSource = useRef(axios.CancelToken.source());

  const fetchMethods = async (_accountID = accountID) => {
    try {
      if (!_accountID) {
        throw new Error('Account ID is required');
      }

      setFetching(true);
      fetchSource.current.cancel('cancelled');
      fetchSource.current = axios.CancelToken.source();

      const res = clientAccountsEnabled
        ? await api.fetchClientAccountPaymentMethods(
            {
              ClientAccountID: _accountID,
            },
            fetchSource.current,
          )
        : await api.fetchContactPaymentMethods(
            {
              ContactID: _accountID,
            },
            fetchSource.current,
          );

      if (!res.Success) {
        throw new Error(`Couldn't fetch payment methods for ${_accountID}`);
      }

      const accountLabels: Record<PaymentMethodType, string> = {
        BankAccount: 'Bank Account',
        CreditCard: 'Credit Card',
        DebitCard: 'Debit Card',
        Email: 'Interac e-Transfer',
        PhoneNumber: 'Interac e-Transfer',
        GooglePay: 'Google Pay',
        ApplePay: 'Apple Pay',
        PayPal: 'PayPal',
        Venmo: 'Venmo',
        Zelle: 'Zelle',
      };

      const getAccountHolderName = (m: VoPaymentMethod) => {
        if (m.PaymentMethod === 'BankAccount') return m.ExtraFields.AccountHolderName;

        if (['CreditCard', 'ApplePay', 'GooglePay'].includes(m.PaymentMethod)) return m.ExtraFields.CardHolderName;

        if (['Email', 'PhoneNumber', 'PayPal', 'Venmo', 'Zelle'].includes(m.PaymentMethod)) return m.ExtraFields.Name;

        return '';
      };

      const paymentMethods = res.PaymentMethods.map((m) => {
        return {
          accountHolderName: getAccountHolderName(m),
          type: m.PaymentMethod,
          typeLabel: accountLabels[m.PaymentMethod],
          // Zelle exposes its alias via AliasType; PayPal/Venmo use RecipientType.
          kind: m.ExtraFields?.RecipientType ?? m.ExtraFields?.AliasType,
          addedOn: m.AddedOn,
          details: m.PaymentDetails || '',
          isDefault: m.IsDefault,
          token: m.Token,
          currency: m.Currency,
          verificationStatus: m.ExtraFields?.VerificationStatus,
          institutionName: m.ExtraFields?.InstitutionName,
          bankId: m.ExtraFields?.BankID,
          creditCardBrand: m.ExtraFields?.CardBrand,
          expirationDate: m.ExtraFields?.ExpirationDate,
          verificationSentOn: m.ExtraFields?.VerificationSentOn,
          hasQuestionAndAnswer: m.ExtraFields?.HasQuestionAndAnswer,
          paymentRails: m.ExtraFields.PaymentRails,
        };
      }).map((m) => ({
        ...m,
        details: m.creditCardBrand
          ? `${creditCardMap[m.creditCardBrand]} **** ${m.details.replace(/\D/g, '').replace(/\d*(\d{4})$/, '$1')}`
          : m.type === 'PhoneNumber' || m.kind === 'PHONE' || m.kind === 'MOBILE'
            ? formatPhoneNumber(m.details)
            : m.type === 'Email'
              ? m.details.toLowerCase()
              : m.details,
      }));

      const sortedMethods = sortPaymentMethods(paymentMethods);
      const group = sortPaymentMethods(groupPaymentMethods(paymentMethods));
      setMethods(sortedMethods);
      setGroupedMethods(group);
      return sortedMethods;
    } catch (err) {
      console.error(err);

      if (!axios.isCancel(err)) {
        throw err;
      }
    } finally {
      setFetching(false);
    }
  };

  function groupPaymentMethods(data: Array<PaymentMethod>) {
    type PaymentMethodBase = Omit<PaymentMethod, 'groupedItems'>;

    type GroupedPayment = PaymentMethodBase & {
      isPlaceholder?: boolean;
      isCreditCardGroupPlaceholder?: boolean;
      groupedItems?: GroupedPayment[];
    };

    const groupedPayments = {} as Record<string, GroupedPayment>; // New structure to group primary contact types: Email, PhoneNumber
    const creditCardGroups = {} as Record<string, GroupedPayment>; // New structure to group credit cards
    const otherItems: Array<GroupedPayment> = [];

    data.forEach((item) => {
      const holderName = item.accountHolderName ? item.accountHolderName.trim() : '';
      const currency = item.currency ? item.currency.trim() : 'N/A_CURRENCY';

      const isPrimaryContactType = item.type === 'Email' || item.type === 'PhoneNumber';
      const isLinkedContactType = item.type === 'PayPal' || item.type === 'Venmo' || item.type === 'Zelle';
      const isCreditCardType = item.type === 'CreditCard' || item.type === 'GooglePay' || item.type === 'ApplePay';

      if (isPrimaryContactType) {
        const key = `${item.type}-${item.details}-${holderName}-${currency}`;

        if (!groupedPayments[key]) {
          groupedPayments[key] = { ...item, groupedItems: [] };
        } else {
          if (groupedPayments[key].isPlaceholder && item.addedOn) {
            const currentGroupedItems = groupedPayments[key].groupedItems;
            groupedPayments[key] = { ...item, groupedItems: currentGroupedItems };
            delete groupedPayments[key].isPlaceholder;
          }
        }
      } else if (isLinkedContactType) {
        let primaryIdentifier;
        if (item.kind === 'EMAIL') {
          primaryIdentifier = 'Email';
        } else if (item.kind === 'PHONE' || item.kind === 'MOBILE') {
          // Zelle aliases use 'MOBILE' where Venmo/PayPal use 'PHONE'; both group under PhoneNumber.
          primaryIdentifier = 'PhoneNumber';
        }

        const key = `${primaryIdentifier}-${item.details}-${holderName}-${currency}`;

        if (!groupedPayments[key]) {
          groupedPayments[key] = {
            accountHolderName: holderName,
            type: primaryIdentifier as PaymentMethodType,
            typeLabel: primaryIdentifier === 'Email' ? 'Interac e-Transfer' : 'Interac e-Transfer',
            details: item.details,
            isDefault: false,
            token: '',
            currency: item.currency || null,
            hasQuestionAndAnswer: item.hasQuestionAndAnswer || false,
            isPlaceholder: true,
            groupedItems: [],
            addedOn: item.addedOn,
          };
        }
        groupedPayments[key]?.groupedItems?.push(item);
      } else if (isCreditCardType) {
        // Grouping for CreditCard, GooglePay, ApplePay
        const cardDetailsKey = item.details; // The last 4 digits
        const cardGroupKey = `${cardDetailsKey}-${holderName}`; // Key includes details and holderName

        if (!creditCardGroups[cardGroupKey]) {
          // Creates a "header" for the card group, preferring CreditCard as the primary type
          let primaryCardObject = null;
          if (item.type === 'CreditCard') {
            primaryCardObject = { ...item, groupedItems: [] };
          } else {
            // If the first item is GooglePay/ApplePay, creates a generic placeholder
            primaryCardObject = {
              accountHolderName: holderName,
              type: 'CreditCard',
              typeLabel: 'Credit Card',
              kind: 'HOLDER', // Generic "parent" type for card grouping
              isCreditCardGroupPlaceholder: true, // New flag for card placeholders
              details: item.details,
              isDefault: false,
              token: '',
              currency: null,
              creditCardBrand: item.creditCardBrand || null,
              expirationDate: item.expirationDate || null,
              groupedItems: [],
              addedOn: item.addedOn,
            } as GroupedPayment;
          }
          creditCardGroups[cardGroupKey] = primaryCardObject;
        } else {
          // If a group already exists, and the current item is a CreditCard and the parent is a placeholder, update the parent
          if (item.type === 'CreditCard' && creditCardGroups[cardGroupKey].isCreditCardGroupPlaceholder) {
            const currentGroupedItems = creditCardGroups[cardGroupKey].groupedItems;
            creditCardGroups[cardGroupKey] = { ...item, groupedItems: currentGroupedItems };
            delete creditCardGroups[cardGroupKey].isCreditCardGroupPlaceholder;
          }
        }
        creditCardGroups[cardGroupKey].groupedItems?.push(item);
      } else {
        // Items that don't fit into any grouping
        otherItems.push(item);
      }
    });

    const finalResult = [];

    // Adds Email/PhoneNumber groupings
    for (const key in groupedPayments) {
      const primaryItem = groupedPayments[key];

      // Logic to "ungroup" solitary PayPal/Venmo items

      if (primaryItem.isPlaceholder && primaryItem.groupedItems?.length === 1) {
        const originalLinkedItem = primaryItem.groupedItems?.[0] as GroupedPayment;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { isPlaceholder, ...cleanOriginalLinkedItem } = originalLinkedItem;
        finalResult.push({ ...cleanOriginalLinkedItem, groupedItems: [] });
      } else {
        if (primaryItem.isPlaceholder && primaryItem.groupedItems?.length && primaryItem.groupedItems.length > 1) {
          // Promote the first grouped item to be the primary and remove it from the groupedItems list
          const firstGrouped = primaryItem.groupedItems.shift()!;
          const remaining = primaryItem.groupedItems;
          // Copy the first group's properties onto the primary item, keeping the remaining groupedItems
          Object.assign(primaryItem, { ...firstGrouped, groupedItems: remaining });
          delete primaryItem.isPlaceholder;
        }
        finalResult.push(primaryItem);
      }
    }

    // Adds CreditCard/GooglePay/ApplePay groupings
    for (const key in creditCardGroups) {
      const primaryCardGroup = creditCardGroups[key];

      // Removes the parent item from within groupedItems if it's the item that initiated the group
      // and there are no other items to group, or if it's a solitary placeholder.
      if (primaryCardGroup.isCreditCardGroupPlaceholder && primaryCardGroup.groupedItems?.length === 1) {
        const originalCardItem = primaryCardGroup.groupedItems[0];
        // Adds the original item (solitary GooglePay/ApplePay) to the root
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { isCreditCardGroupPlaceholder, ...cleanOriginalCardItem } = originalCardItem;
        finalResult.push({ ...cleanOriginalCardItem, groupedItems: [] });
      } else {
        // If the item that initiated the group is also in groupedItems, remove it to avoid duplication.
        // This happens if the first item processed for a group is the "parent" of the group.
        primaryCardGroup.groupedItems = primaryCardGroup.groupedItems?.filter(
          (groupedItem) =>
            !(
              groupedItem.type === primaryCardGroup.type &&
              groupedItem.details === primaryCardGroup.details &&
              (groupedItem.accountHolderName || '').trim() === (primaryCardGroup.accountHolderName || '').trim() &&
              !primaryCardGroup.isCreditCardGroupPlaceholder // Do not filter if it's a placeholder, as it's not the "real" item
            ),
        );
        // Ensures the parent item is not added if it has already been handled as a grouping
        if (
          (primaryCardGroup.groupedItems?.length && primaryCardGroup.groupedItems.length > 0) ||
          !primaryCardGroup.isCreditCardGroupPlaceholder
        ) {
          // Removes the isCreditCardGroupPlaceholder flag if it's no longer a solitary placeholder
          if (
            primaryCardGroup.isCreditCardGroupPlaceholder &&
            primaryCardGroup.groupedItems?.length &&
            primaryCardGroup.groupedItems.length > 0
          ) {
            delete primaryCardGroup.isCreditCardGroupPlaceholder;
          }
          finalResult.push(primaryCardGroup);
        } else if (primaryCardGroup.isCreditCardGroupPlaceholder && primaryCardGroup.groupedItems?.length === 0) {
          // This case would be for an empty placeholder that never received items; it should not be added
        }
      }
    }

    // Adds other ungrouped items
    finalResult.push(...otherItems);

    return finalResult;
  }

  function sortPaymentMethods(arr: PaymentMethod[]) {
    // eventually we want the first (0th) entry to be the primary currency.
    const currencyOrder = currencies;
    const typeOrder: PaymentMethodType[] = ['BankAccount', 'CreditCard', 'DebitCard', 'Email', 'PhoneNumber'];

    return [...arr].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;

      if (b.isDefault && !a.isDefault) return 1;

      // set it to CAD (eventually the primary currency) so that we can compare properly.
      const aCurrency = a.currency === null ? currencies[0] : a.currency;
      const bCurrency = b.currency === null ? currencies[0] : b.currency;

      const aaCurrencyOrder = currencyOrder.indexOf(aCurrency);
      const bbCurrencyOrder = currencyOrder.indexOf(bCurrency);

      if (aaCurrencyOrder !== bbCurrencyOrder) return aaCurrencyOrder - bbCurrencyOrder;

      return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
    });
  }

  const setDefaultMethod = (method: PaymentMethodType, accountToken: string, _accountID = accountID) => {
    if (!_accountID) {
      return Promise.resolve({
        Success: false,
        ErrorMessage: 'Account ID is required',
      });
    }

    try {
      setPending(true);

      if (method === 'BankAccount') {
        // TODO: b/e to accept ContactID as well
        return api.postSetMyClientBankAccount(
          {
            ClientAccountID: accountID,
            SetAsDefault: true,
            Token: accountToken,
            PortalUser: loginUserName,
          },
          source.current,
        );
      }

      if (method === 'CreditCard') {
        // TODO: b/e to accept ContactID as well
        return api.postSetClientMyCreditCard(
          {
            CreditCardToken: accountToken,
            SetAsDefault: true,
          },
          source.current,
        );
      }

      if (method === 'DebitCard') {
        // TODO: b/e to accept ContactID as well
        return api.postSetClientMyDebitCard(
          {
            CardToken: accountToken,
            SetAsDefault: true,
          },
          source.current,
        );
      }

      if (method === 'Email' || method === 'PhoneNumber') {
        // TODO: b/e to accept ContactID as well
        return api.postSetClientEmail(
          {
            Token: accountToken,
            ClientAccountID: _accountID,
            SetAsDefault: true,
          },
          source.current,
        );
      }

      if (method === 'PayPal') {
        return api.postSetClientPayPal(
          {
            PayPalToken: accountToken,
            ClientAccountID: _accountID,
            SetAsDefault: true,
          },
          source.current,
        );
      }

      if (method === 'Venmo') {
        return api.postSetClientVenmo(
          {
            VenmoToken: accountToken,
            ClientAccountID: _accountID,
            SetAsDefault: true,
          },
          source.current,
        );
      }

      if (method === 'Zelle') {
        return api.setZelleClient(
          {
            ZelleToken: accountToken,
            ClientAccountID: _accountID,
            SetAsDefault: true,
          },
          source.current,
        );
      }

      if (method === 'GooglePay') {
        return api.postSetClientGooglePay(
          {
            GooglePayToken: accountToken,
            ClientAccountID: _accountID,
            SetAsDefault: true,
          },
          source.current,
        );
      }

      if (method === 'ApplePay') {
        return api.postSetClientApplePay(
          {
            ApplePayToken: accountToken,
            ClientAccountID: _accountID,
            SetAsDefault: true,
          },
          source.current,
        );
      }

      return Promise.resolve({
        Success: false,
        ErrorMessage: '',
      });
    } finally {
      setPending(false);
    }
  };

  // the rest of the methods are added through iQ11
  const addInterac = ({
    email,
    phone,
    question,
    answer,
    recipientName,
    accountID: _accountID = accountID,
  }: AddInteracParams) => {
    try {
      setPending(true);

      return api.postAddInteracEmail(
        {
          PortalUser: loginUserName,
          [CUSTOMER_ID_PROP]: _accountID,
          RecipientName: recipientName,
          RecipientEmailAddress: email,
          RecipientPhoneNumber: phone,
          Question: question,
          Answer: answer,
          ClientControlled: clientAccountsEnabled,
        },
        source.current,
      );
    } finally {
      setPending(false);
    }
  };

  return {
    methods,
    groupedMethods,
    fetchMethods,
    fetching,
    setDefaultMethod,
    deleteMethod,
    deleteGroupedMethod,
    addInterac,
  };
}
